// THE OVERRIDE POP-UP (owner, 2026-10-12 — docs/decisions.md D-350, amended
// the same day), opened from "Click here to override." after the route line
// beside Release at Import Control and Reinstate at Denial Review.
//
// The CFO's request — let the controllers direct an invoice themselves — and
// the owner's — "if staff is going to be following some logic, then that logic
// should be built into the system" — meet here: the pop-up says what the route
// follows now, takes any combination of the four steps (the same picture the
// Add/Edit Rule pop-up draws), and a box, checked by default, that makes the
// choice the vendor's own rule. No Category and no Comment (amended: "It's a
// bit busy, and the CFO hasn't asked for justification behind rules like these
// yet").
//
// THREE BUTTONS: Cancel; Save, which stores the Override and moves nothing (the
// next Release or Reinstate applies it); and Save & Release (Save & Reinstate
// at Denial Review), always shown, green when the invoice is ready to go and
// disabled with the editor's own reasons as its tooltip when it is not
// (amended, owner item 7). The editor does the work: it is the editor's own
// Release, or its own Reinstate confirmation.
//
// THE POP-UP FORM SPACING (D-349): parts 16px apart, buttons 24px below. The
// step picture and its lead line are spaced as in the Add/Edit Rule pop-up
// (`useDialogFormStyles().routeLead` / `routeDiagram`). The lead line is in
// regular weight, with a hairline in the space above it (owner, 2026-10-12 —
// D-350 amended: "in the space above this sentence, try a hairline divider"):
// Fluent's `Divider`, as the Vendor pop-up draws its address groups, 16px
// below what is above it and 16px above the line.

import { useEffect, useState } from 'react'
import {
  Body1,
  Button,
  Checkbox,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { DEFAULT_REQUIRED_APPROVALS, REQUIRED_APPROVAL_FIELD } from '@ap/shared'
import type { RequiredApprovals, RouteOverride, RouteOverrideWrite, RoutingPreview } from '../api/types'
import { AppDialog } from './AppDialog'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { ApprovalPathPicker } from './Timeline'
import { useDialogFormStyles } from './dialogForm'
import { toggleRequiredApproval } from '../lib/oversightRules'
import { ROUTING_RULE_CLOSING } from '../lib/oversightRuleSentence'
import { useDecisionButtonStyles } from '../lib/theme'
import { CREATE_RULE_LABEL, currentRouteExplanation, overrideApprovals } from '../lib/routeOverride'

const useStyles = makeStyles({
  // Wide enough for the four steps drawn as the Pipeline draws them.
  surface: { maxWidth: 'min(640px, calc(100vw - 48px))', width: 'min(640px, calc(100vw - 48px))' },
  rules: { margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  // The hairline above "Instead, require the following approvals:". The
  // group's 4px gap and the line's 12px top margin put the line 16px below it.
  rule: { marginTop: 0, marginBottom: 0 },
  // The label sits before the box (owner, 2026-10-12: the label TO THE LEFT
  // of the checkbox); Fluent's own label padding keeps it off the box.
  checkbox: { marginLeft: `calc(-1 * ${tokens.spacingHorizontalS})` },
  // "Remember this route…" sits as far below "Manager Approval" as "Instead,
  // require the following approvals:" sits above the Controller Approval
  // circle: 17px (owner, 2026-10-13 — D-352). Measured in the browser, the
  // step's own padding, the group's margin and the stack gap left it 50px.
  remember: { marginTop: '-33px' },
  error: { color: tokens.colorPaletteRedForeground1 },
  button: { whiteSpace: 'nowrap', flexShrink: 0 },
})

interface Props {
  open: boolean
  /** The Override on the invoice now, if any. */
  override: RouteOverride | null
  /** What the rules or the default route say, the Override left out. */
  preview: RoutingPreview | null
  /** Whether the invoice has a vendor: without one, no rule can be written. */
  hasVendor: boolean
  saving: boolean
  error: string | null
  /** "Save & Release" at Import Control, "Save & Reinstate" at Denial Review. */
  releaseLabel: string
  /** The editor's own gate: `canSaveAndRelease`, or `canReinstate`. */
  canRelease: boolean
  /** Release's (or Reinstate's) disabled reasons, its tooltip as on the editor's button. */
  releaseBlockedTitle: string | undefined
  releasing: boolean
  onCancel: () => void
  onSave: (override: RouteOverrideWrite) => void
  onSaveAndRelease: (override: RouteOverrideWrite) => void
  /** Clears the Override; offered only when one is set. */
  onRemove: () => void
}

export function RouteOverrideDialog({
  open,
  override,
  preview,
  hasVendor,
  saving,
  error,
  releaseLabel,
  canRelease,
  releaseBlockedTitle,
  releasing,
  onCancel,
  onSave,
  onSaveAndRelease,
  onRemove,
}: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const btn = useDecisionButtonStyles()
  const [approvals, setApprovals] = useState<RequiredApprovals>({ ...DEFAULT_REQUIRED_APPROVALS })
  const [createRule, setCreateRule] = useState(true)

  // Opens on the route as it stands: the Override's own answer when one is
  // set, else what the rules or the default route require.
  useEffect(() => {
    if (!open) return
    setApprovals(override ? overrideApprovals(override) : { ...(preview?.required ?? DEFAULT_REQUIRED_APPROVALS) })
    // Checked by default (owner, 2026-10-12: "I would check the invoice by
    // default, since they are taking the time to spell it out.").
    setCreateRule(override?.create_rule ?? true)
    // Seeded once per opening; a preview answering later must not undo a pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const busy = saving || releasing
  const explanation = currentRouteExplanation(override, preview)
  const write = (): RouteOverrideWrite => ({ ...approvals, create_rule: hasVendor && createRule })
  const releaseEnabled = canRelease && !busy

  return (
    <AppDialog open={open} onOpenChange={(_, d) => { if (!d.open && !busy) onCancel() }}>
      <DialogSurface className={s.surface} backdrop={{ appearance: 'dimmed' }} aria-label="Override">
        <FrozenWhileClosed open={open}><DialogBody>
          <DialogTitle>Override</DialogTitle>
          <DialogContent className={f.beforeActions}>
            <div className={f.stack}>
              {error && <Body1 className={s.error}>{error}</Body1>}
              <div>
                <Body1 block>{explanation.lead}</Body1>
                {explanation.rules.length > 0 && (
                  <ul className={s.rules}>
                    {explanation.rules.map((rule, i) => (
                      <li key={i}>
                        <Body1>{rule}</Body1>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* The same picture, and the same one-click toggles, as the
                  Add/Edit Rule pop-up (owner: "let's duplicate that checkbox
                  diagram"), spaced as it is there. Controller and Manager stay
                  one or the other. */}
              <div className={f.routeGroup}>
                <Divider className={s.rule} />
                <Body1 block className={f.routeLead}>
                  {`Instead, ${ROUTING_RULE_CLOSING}`}
                </Body1>
                <div className={f.routeDiagram}>
                  <ApprovalPathPicker
                    required={{
                      controller: approvals[REQUIRED_APPROVAL_FIELD.controller],
                      manager: approvals[REQUIRED_APPROVAL_FIELD.manager],
                      director: approvals[REQUIRED_APPROVAL_FIELD.director],
                      oversight: approvals[REQUIRED_APPROVAL_FIELD.oversight],
                    }}
                    onToggle={(key, on) => setApprovals((current) => toggleRequiredApproval(current, key, on))}
                  />
                </div>
              </div>
              <div className={s.remember}>
                {/* With no Vendor the box cannot work: the reason is its tooltip,
                    not a line under it (no explainer text under a control). */}
                {hasVendor ? (
                  <Checkbox
                    className={s.checkbox}
                    labelPosition="before"
                    checked={createRule}
                    onChange={(_, d) => setCreateRule(d.checked === true)}
                    label={CREATE_RULE_LABEL}
                  />
                ) : (
                  <Tooltip content="Set the Vendor to remember a route." relationship="description">
                    <span>
                      <Checkbox className={s.checkbox} labelPosition="before" checked={false} disabled label={CREATE_RULE_LABEL} />
                    </span>
                  </Tooltip>
                )}
                {/* No line naming the vendor rule the box would replace (owner,
                    2026-10-13 — D-355): the lead line at the top already says
                    which rules route the invoice, and an Inactive rule is not
                    the user's concern. */}
              </div>
            </div>
          </DialogContent>
          {override && (
            <DialogActions position="start">
              <Button appearance="secondary" className={s.button} onClick={onRemove} disabled={busy}>
                Remove Override
              </Button>
            </DialogActions>
          )}
          <DialogActions>
            <Button appearance="secondary" className={s.button} onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button appearance="primary" className={s.button} disabled={busy} onClick={() => onSave(write())}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {/* Always shown; green only when the invoice can go (owner item 7),
                the green of Approve (`useDecisionButtonStyles`), never a
                look-alike. */}
            <Button
              appearance="primary"
              className={mergeClasses(s.button, btn.approve)}
              disabled={!releaseEnabled}
              title={canRelease ? undefined : releaseBlockedTitle}
              aria-label={canRelease || !releaseBlockedTitle ? releaseLabel : `${releaseLabel} — ${releaseBlockedTitle}`}
              onClick={() => onSaveAndRelease(write())}
            >
              {releasing ? 'Releasing…' : releaseLabel}
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
